import { AxiosResponse } from 'axios';
import i18next from 'i18next';
import { hideLoading, showLoading } from 'react-redux-loading-bar';
import { push } from 'react-router-redux';
import { call, put, select } from 'redux-saga/effects';
import { RootState, store } from 'src/app/store';
import CheckCircleBig from 'src/assets/check-circle-big.svg';
import { AdminSettingsFormData } from 'src/components/pages/Settings/AdminSettings/AdminSettings';
import { routes } from 'src/routes';
import { cleanQueryParams } from 'src/utils/useQueryParams';

import instance, { CustomAxiosError } from '../../axios';
import { apiBaseRoutes, apiRoutes } from '../../constants/apiConstants';
import { Errors } from '../../constants/errorConstants';
import {
  addError,
  addModal,
  addSnackbar,
  removeFile,
  removeModal,
  setGlobalLoading,
  setRememberMe,
  startLoading,
  stopLoading,
} from '../../features/globalSlice';
import {
  changePassword,
  changeSettings,
  changeTheme,
  checkUser,
  editSupportInfo,
  getSupportInfo,
  login,
  loginSuccess,
  logout,
  sendNewPassword,
  setAllUsers,
  setAuthCheck,
  setLoginSuccess,
  setSupportInfo,
  switchManufacturer,
  syncUser,
} from '../actions/authActions';
import { GlobalFile } from '../models/Action';
import { CheckUser, SupportInfo, SyncUser, User } from '../models/Auth';
import { ModalType } from '../models/Modal';
import { SnackbarType } from '../models/Snackbar';
import { ApiResponse } from '.';
import { GlobalEditDynamicSettingsSaga } from './globalSaga';

/* Login */
export function* AuthLoginSaga(action: ReturnType<typeof login>): Generator {
  yield put(setGlobalLoading(true));
  try {
    const response = (yield instance.post(
      `${apiBaseRoutes.USER}${apiRoutes.LOGIN}`,
      {
        eMail: action.payload?.eMail,
        password: action.payload?.password,
        mId: action.payload?.manufacturer?.id,
      },
      {}
    )) as AxiosResponse<ApiResponse<User>>;

    yield put(setRememberMe(action.payload.rememberMe));
    yield put(loginSuccess(response.data.data));
  } catch (e) {
    const error = e as CustomAxiosError;
    yield put(
      addError({
        actionType: action.type,
        error: error.response?.data?.message || Errors.UNKNOWN_ERROR,
      })
    );
  } finally {
    yield put(setGlobalLoading(false));
  }
}

/* Check user */
export function* AuthCheckUserSaga(
  action: ReturnType<typeof checkUser>
): Generator {
  yield put(setGlobalLoading(true));
  try {
    const response = (yield instance.post(
      `${apiBaseRoutes.USER}${apiRoutes.CHECK}`,
      {
        eMail: action.payload.email,
      }
    )) as AxiosResponse<ApiResponse<CheckUser[]>>;

    yield put(setAuthCheck(response.data.data));
    yield put(setLoginSuccess(true));
  } catch (e) {
    const error = e as CustomAxiosError;
    yield put(
      addError({
        actionType: action.type,
        error: error.response?.data?.message || Errors.UNKNOWN_ERROR,
      })
    );
  } finally {
    yield put(setGlobalLoading(false));
  }
}

/* Sync user */
export function* AuthSyncUserSaga(
  action: ReturnType<typeof syncUser>
): Generator {
  yield put(showLoading());
  try {
    const state = (yield select()) as RootState;

    const response = (yield instance.get(
      `${apiBaseRoutes.USER}${apiRoutes.SYNC}`
    )) as AxiosResponse<ApiResponse<SyncUser[]>>;

    if (Array.isArray(response?.data?.data)) {
      yield put(
        loginSuccess(
          response.data.data?.find((u) => u?.id === state?.auth?.user?.id)
        )
      );
      yield put(
        setAllUsers(
          response?.data?.data?.filter(
            (u) => u?.role === state?.auth?.user?.role && u?.enabled
          ) || []
        )
      );
    } else {
      yield put(loginSuccess(response.data.data));
      yield put(setAllUsers([response?.data?.data]));
    }
  } catch (e) {
    const error = e as CustomAxiosError;
    yield put(
      addError({
        actionType: action.type,
        error: error.response?.data?.message || Errors.UNKNOWN_ERROR,
      })
    );
  } finally {
    yield put(hideLoading());
  }
}

/* Change password */
export function* AuthChangePasswordSaga(
  action: ReturnType<typeof changePassword>
): Generator {
  yield put(showLoading());
  yield put(startLoading({ actionType: action.type }));
  try {
    const response = (yield instance.put(
      `${apiBaseRoutes.USER}${apiRoutes.PASSWORD}`,
      {
        ...action.payload,
      }
    )) as AxiosResponse<ApiResponse<User>>;

    yield put(loginSuccess(response.data.data));
    yield put(
      addModal({
        icon: CheckCircleBig,
        title: i18next.t('common:done'),
        body: i18next.t('settings:password_changed'),
        modalType: ModalType.SUCCESS,
        secondaryButtonText: i18next.t('common:close'),
        secondaryAction: () => store.dispatch(removeModal()),
      })
    );
  } catch (e) {
    const error = e as CustomAxiosError;
    const errorMessage = error.response?.data?.message || Errors.UNKNOWN_ERROR;
    yield put(
      addError({
        actionType: action.type,
        error: errorMessage,
      })
    );
  } finally {
    yield put(hideLoading());
    yield put(stopLoading({ actionType: action.type }));
  }
}

/* Change image and/or title */
function* AuthChangeImageAndOrTitle(
  image: GlobalFile,
  title: string
): Generator {
  try {
    const response = (yield instance.put(`${apiBaseRoutes.USER}`, {
      image: image?.url,
      title,
    })) as AxiosResponse<ApiResponse<SyncUser[]>>;

    return response;
  } catch (e) {
    const error = e as CustomAxiosError;
    const errorMessage = error.response?.data?.message || Errors.UNKNOWN_ERROR;
    yield put(
      addSnackbar({
        type: SnackbarType.ERROR,
        body: errorMessage,
        snackKey: Date.now(),
      })
    );
  }
}

/* Edit support info */
function* AuthEditSupportInfoSaga(
  settingsData: AdminSettingsFormData
): Generator {
  yield put(startLoading({ actionType: editSupportInfo.type }));
  try {
    const response = (yield instance.post(`${apiBaseRoutes.SUPPORT_INFO}`, {
      eMail: settingsData?.eMail,
      phone: settingsData?.phone,
      website: settingsData?.website,
    })) as AxiosResponse<ApiResponse<SupportInfo>>;

    yield put(setSupportInfo(response?.data?.data));
    yield put(
      addSnackbar({
        type: SnackbarType.SUCCESS,
        title: i18next.t('common:changes_saved'),
        body: i18next.t('common:changes_saved_success'),
        snackKey: Date.now(),
      })
    );
  } catch (e) {
    const error = e as CustomAxiosError;
    const errorMessage = error.response?.data?.message || Errors.UNKNOWN_ERROR;
    if (typeof error.response?.data?.message === 'string') {
      yield put(
        addSnackbar({
          type: SnackbarType.ERROR,
          body: errorMessage,
          snackKey: Date.now(),
        })
      );
    } else {
      yield put(
        addError({
          actionType: editSupportInfo.type,
          error: errorMessage,
        })
      );
    }
  } finally {
    yield put(stopLoading({ actionType: editSupportInfo.type }));
  }
}

/* Change settings */
export function* AuthChangeSettingsSaga(
  action: ReturnType<typeof changeSettings>
): Generator {
  yield put(showLoading());
  yield put(setGlobalLoading(true));
  try {
    const state = (yield select()) as RootState;

    /* Edit dynamic settings */
    if (action?.payload?.dynamicSettings) {
      yield call(
        GlobalEditDynamicSettingsSaga,
        action?.payload?.dynamicSettings
      );
    }

    const response = (yield call(
      AuthChangeImageAndOrTitle,
      action.payload?.image,
      action?.payload?.settingsData?.title
    )) as AxiosResponse<ApiResponse<SyncUser[]>>;

    if (Array.isArray(response?.data?.data)) {
      yield put(
        loginSuccess(
          response.data.data?.find((u) => u?.id === state?.auth?.user?.id)
        )
      );
    } else {
      yield put(loginSuccess(response.data.data));
    }
    yield put(removeFile({ id: action.payload?.image?.id }));

    if (action.payload?.settingsData) {
      yield call(AuthEditSupportInfoSaga, action.payload?.settingsData);
    } else {
      yield put(
        addSnackbar({
          type: SnackbarType.SUCCESS,
          title: i18next.t('common:changes_saved'),
          body: i18next.t('common:changes_saved_success'),
          snackKey: Date.now(),
        })
      );
    }
  } catch (e) {
    const error = e as CustomAxiosError;
    const errorMessage = error.response?.data?.message || Errors.UNKNOWN_ERROR;
    yield put(
      addError({
        actionType: action.type,
        error: errorMessage,
      })
    );
  } finally {
    yield put(hideLoading());
    yield put(setGlobalLoading(false));
  }
}

/* Switch manufacturer */
export function* AuthSwitchManufacturerSaga(
  action: ReturnType<typeof switchManufacturer>
): Generator {
  try {
    const state = (yield select()) as RootState;

    yield put(
      addModal({
        title: `${i18next.t('login:logging_out')} ...`,
        body: i18next.t('login:switch_accounts_logout'),
        modalType: ModalType.WARNING,
        primaryButtonText: i18next.t('common:continue'),
        secondaryButtonText: i18next.t('common:cancel'),
        primaryAction: () => {
          store.dispatch(removeModal());
          store.dispatch(
            push(
              {
                pathname: routes.LOGIN,
                search: cleanQueryParams({
                  email: state?.auth?.user?.eMail,
                  manId: action?.payload?.manId,
                }),
              },
              {
                forcePush: true,
              }
            )
          );
          store.dispatch(logout());
        },
        secondaryAction: () => {
          store.dispatch(removeModal());
        },
      })
    );
  } catch (e) {
    const error = e as CustomAxiosError;
    const errorMessage = error.response?.data?.message || Errors.UNKNOWN_ERROR;
    yield put(
      addSnackbar({
        type: SnackbarType.ERROR,
        body: errorMessage,
        snackKey: Date.now(),
      })
    );
  }
}

/* Get support info */
export function* AuthGetSupportInfoSaga(
  action: ReturnType<typeof getSupportInfo>
): Generator {
  yield put(showLoading());
  yield put(startLoading({ actionType: action.type }));
  try {
    const response = (yield instance.get(
      `${apiBaseRoutes.SUPPORT_INFO}`
    )) as AxiosResponse<ApiResponse<SupportInfo>>;

    yield put(setSupportInfo(response?.data?.data));
  } catch (e) {
    const error = e as CustomAxiosError;
    const errorMessage = error.response?.data?.message || Errors.UNKNOWN_ERROR;
    yield put(
      addSnackbar({
        type: SnackbarType.ERROR,
        body: errorMessage,
        snackKey: Date.now(),
      })
    );
  } finally {
    yield put(hideLoading());
    yield put(stopLoading({ actionType: action.type }));
  }
}

/* Reset password helper */
export function* ResetPasswordHelper(userId: string): Generator {
  try {
    const response = (yield instance.put(
      `${apiBaseRoutes.RESET}/${userId}`
    )) as AxiosResponse<ApiResponse<void>>;
    return response;
  } catch (e) {
    const error = e as CustomAxiosError;
    const errorMessage = error.response?.data?.message || Errors.UNKNOWN_ERROR;
    yield put(
      addSnackbar({
        type: SnackbarType.ERROR,
        body: errorMessage,
        snackKey: Date.now(),
      })
    );
  }
}

/* Send new password */
export function* AuthSendNewPasswordSaga(
  action: ReturnType<typeof sendNewPassword>
): Generator {
  yield put(showLoading());
  yield put(startLoading({ actionType: action.type }));
  try {
    const response = (yield call(
      ResetPasswordHelper,
      action?.payload?.userId
    )) as AxiosResponse<ApiResponse<void>>;

    if (response) {
      yield put(
        addModal({
          title: i18next.t('login:password_sent'),
          body: i18next.t('login:password_sent_details', {
            title: action?.payload?.title,
          }),
          modalType: ModalType.SUCCESS,
          icon: CheckCircleBig,
          secondaryButtonText: i18next.t('common:close'),
          secondaryAction: () => {
            store.dispatch(removeModal());
          },
        })
      );
    }
  } catch (e) {
    const error = e as CustomAxiosError;
    const errorMessage = error.response?.data?.message || Errors.UNKNOWN_ERROR;
    yield put(
      addSnackbar({
        type: SnackbarType.ERROR,
        body: errorMessage,
        snackKey: Date.now(),
      })
    );
  } finally {
    yield put(hideLoading());
    yield put(stopLoading({ actionType: action.type }));
  }
}

/* Change theme */
export function* AuthChangeThemeSaga(
  action: ReturnType<typeof changeTheme>
): Generator {
  yield put(showLoading());
  yield put(setGlobalLoading(true));
  try {
    const state = (yield select()) as RootState;

    const response = (yield instance.put(
      `${apiBaseRoutes.USER}${apiRoutes.SETTING}`,
      { theme: action?.payload }
    )) as AxiosResponse<ApiResponse<SyncUser>>;

    yield put(
      loginSuccess({
        ...state?.auth?.user,
        settings: response?.data?.data?.settings,
      })
    );
  } catch (e) {
    const error = e as CustomAxiosError;
    const errorMessage = error.response?.data?.message || Errors.UNKNOWN_ERROR;
    yield put(
      addSnackbar({
        type: SnackbarType.ERROR,
        body: errorMessage,
        snackKey: Date.now(),
      })
    );
  } finally {
    yield put(hideLoading());
    yield put(setGlobalLoading(false));
  }
}
